import type {
  GapDraftReviewConfidence,
  GapDraftReviewStatus,
} from '@momentum/types';

import { pluralize, titleCase } from '../lib/format';
import { DROP_STATUS, KEEP_STATUS } from '../lib/queue';
import { REVIEW_CONFIDENCE_LEVELS } from '../lib/review-confidence';

interface ReaderActionBarProps {
  isKept: boolean;
  isDropped: boolean;
  commentCount: number;
  /** Whether an unposted comment draft exists for this gap. */
  hasCommentDraft: boolean;
  /**
   * The verdict waiting on a confidence level, or null while none is. While it
   * is set the bar is the picker and nothing has been filed yet.
   */
  pendingVerdict: GapDraftReviewStatus | null;
  /** The level the picker opens on — this draft's own, or the default. */
  confidence: GapDraftReviewConfidence;
  /**
   * The level on record for this draft. Absent on one decided before the field
   * existed, which is why it is not the same prop as `confidence`: the picker
   * still has to open on something, but the decided button must not claim a
   * level nobody chose.
   */
  recordedConfidence?: GapDraftReviewConfidence;
  onKeep: () => void;
  onDrop: () => void;
  onPickConfidence: (confidence: GapDraftReviewConfidence) => void;
  /** Reopen the picker on a draft already decided, to re-file at a new level. */
  onChangeConfidence: (verdict: GapDraftReviewStatus) => void;
  onCancelConfirm: () => void;
  onCompose: () => void;
}

/** "Dropped · high", or plain "Dropped" when no level was ever recorded. */
function decidedLabel(
  verb: string,
  recordedConfidence: GapDraftReviewConfidence | undefined
): string {
  return recordedConfidence === undefined
    ? verb
    : `${verb} · ${recordedConfidence}`;
}

/**
 * The confidence prompt the bar becomes between the verdict and the write.
 *
 * A radiogroup rather than three buttons: the three levels are one answer to
 * one question, and the question is on screen beside them.
 */
function ConfidencePrompt({
  pendingVerdict,
  confidence,
  onPickConfidence,
  onCancelConfirm,
}: Pick<
  ReaderActionBarProps,
  'pendingVerdict' | 'confidence' | 'onPickConfidence' | 'onCancelConfirm'
>) {
  const verdictName = pendingVerdict === KEEP_STATUS ? 'keeping' : 'dropping';

  return (
    <div className="gr-confirm" data-verdict={pendingVerdict}>
      <span
        className="gr-confirm-q"
        // Named rather than just "How sure are you?": the bar has replaced the
        // verdict buttons, so the sentence is the only thing still saying which
        // verdict is about to be filed.
        id="gr-confirm-q"
      >
        How sure are you about {verdictName} this gap?
      </span>

      <div
        className="gr-confirm-opts"
        role="radiogroup"
        aria-labelledby="gr-confirm-q"
      >
        {REVIEW_CONFIDENCE_LEVELS.map((level, levelIndex) => (
          <button
            key={level}
            className="gr-conf-opt"
            type="button"
            role="radio"
            aria-checked={level === confidence}
            data-level={level}
            onClick={() => onPickConfidence(level)}
          >
            <span className="gr-kbd">{levelIndex + 1}</span>
            {titleCase(level)}
          </button>
        ))}
      </div>

      <button className="gr-linkbtn" type="button" onClick={onCancelConfirm}>
        Cancel <span className="gr-kbd">Esc</span>
      </button>
    </div>
  );
}

/**
 * The verdict bar, pinned to the bottom of the reader.
 *
 * Filing a verdict takes two steps. Keep and Drop no longer write: they ask how
 * sure the reviewer is, and the level answers with the write. The field is
 * required on both verdicts, and the alternative — a control preselected at
 * medium sitting in the bar — recorded a judgement nobody made every time a
 * reviewer pressed 1 without looking at it. Two keystrokes for a decision worth
 * qualifying is the trade, and the second one is on the same hand.
 *
 * A decided draft says so on the button it was filed with — "Dropped · high" —
 * with a `change` beside it that reopens the picker. So the level survives
 * walking away and coming back, and changing it does not mean restoring the
 * gap and deciding again. The button itself stays a toggle: pressing the
 * verdict a draft already has clears it and returns the draft to the queue,
 * which is the one undo path that does not depend on a toast still being on
 * screen.
 *
 * An unposted comment draft is called out here rather than only in the Notes
 * tab, because it is also what holds auto-advance back — a reviewer who does not
 * see why the queue stopped moving will assume the shortcut broke.
 */
export function ReaderActionBar({
  isKept,
  isDropped,
  commentCount,
  hasCommentDraft,
  pendingVerdict,
  confidence,
  recordedConfidence,
  onKeep,
  onDrop,
  onPickConfidence,
  onChangeConfidence,
  onCancelConfirm,
  onCompose,
}: ReaderActionBarProps) {
  if (pendingVerdict !== null) {
    return (
      <div className="gr-actionbar" data-mode="confirm">
        <ConfidencePrompt
          pendingVerdict={pendingVerdict}
          confidence={confidence}
          onPickConfidence={onPickConfidence}
          onCancelConfirm={onCancelConfirm}
        />
      </div>
    );
  }

  return (
    <div className="gr-actionbar">
      <div className="gr-actionbar-group">
        <button
          className="gr-btn"
          type="button"
          data-role="keep"
          aria-pressed={isKept}
          onClick={onKeep}
        >
          {isKept ? (
            decidedLabel('Kept', recordedConfidence)
          ) : (
            <>
              Keep gap <span>1</span>
            </>
          )}
        </button>
        {isKept && (
          <button
            className="gr-conf-change"
            type="button"
            aria-label="Change the confidence behind keeping this gap"
            onClick={() => onChangeConfidence(KEEP_STATUS)}
          >
            change
          </button>
        )}
      </div>

      <div className="gr-actionbar-group">
        <button
          className="gr-btn"
          type="button"
          data-role="drop"
          aria-pressed={isDropped}
          onClick={onDrop}
        >
          {isDropped ? (
            decidedLabel('Dropped', recordedConfidence)
          ) : (
            <>
              Drop it <span>2</span>
            </>
          )}
        </button>
        {isDropped && (
          <button
            className="gr-conf-change"
            type="button"
            aria-label="Change the confidence behind dropping this gap"
            onClick={() => onChangeConfidence(DROP_STATUS)}
          >
            change
          </button>
        )}
      </div>

      <div className="gr-actionbar-group">
        <i className="gr-actionbar-sep" />
        <span className="gr-count">
          {pluralize(commentCount, 'comment')}
          {hasCommentDraft && (
            <>
              {' · '}
              <span className="gr-dirty">unposted draft</span>
            </>
          )}
        </span>
        <button className="gr-linkbtn" type="button" onClick={onCompose}>
          Add comment <span className="gr-kbd">C</span>
        </button>
      </div>
    </div>
  );
}
